import React, { useState, useMemo, useEffect } from 'react';
import { KatButton, KatModal, KatSpinner } from '@amzn/katal-react';
import { BatcherService, FunctionMetadata, TERMINAL_JOB_STATUSES } from '../../services/Batcher/BatcherService';
import './BatcherPage.scss';

// How often to poll the job status while a submitted job is still running.
const JOB_POLL_INTERVAL_MS = 3000;

interface BatcherPageProps {
    stage: string;
    realm: string;
}

type StepId = 'functions' | 'input' | 'submit';

const STEPS: { id: StepId; label: string }[] = [
    { id: 'functions', label: 'Select Functions' },
    { id: 'input', label: 'Input' },
    { id: 'submit', label: 'Submit' },
];

const BatcherPage: React.FC<BatcherPageProps> = ({ stage, realm }) => {
    // Functions the user has added to the chain
    const [chain, setChain] = useState<FunctionMetadata[]>([]);

    // Which step/tab is active. Navigation only happens via the Back/Next buttons.
    const [activeStep, setActiveStep] = useState<StepId>('functions');

    // Modal + picker state
    const [modalOpen, setModalOpen] = useState(false);
    const [availableFunctions, setAvailableFunctions] = useState<FunctionMetadata[]>([]);
    // Functions are fetched once (on first open) and cached for subsequent opens.
    const [functionsLoaded, setFunctionsLoaded] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // CSV row input. The header is derived from the selected functions (see csvHeader
    // below) and is not editable.
    const [csvRows, setCsvRows] = useState('');

    // Submit state
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [jobId, setJobId] = useState('');

    // Job status polling state (populated after a successful submit).
    const [jobStatus, setJobStatus] = useState('');
    const [downloadUrl, setDownloadUrl] = useState('');
    const [jobError, setJobError] = useState('');
    // Row count captured at submit time — the denominator for the progress bar.
    const [submittedRowCount, setSubmittedRowCount] = useState(0);

    const PAGE_SIZE = 10;

    const filteredFunctions = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return availableFunctions;
        return availableFunctions.filter(fn => fn.functionName.toLowerCase().includes(term));
    }, [availableFunctions, search]);

    const totalPages = Math.max(1, Math.ceil(filteredFunctions.length / PAGE_SIZE));
    // Clamp the page in case filtering shrank the list below the current page.
    const currentPage = Math.min(page, totalPages - 1);
    const pageFunctions = filteredFunctions.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

    // CSV header: union of the selected functions' INPUT field names, in first-seen order.
    // Derived from the chain and immutable — JsonPath-style functions expect a single JSON
    // object column, so a fixed header is always correct.
    const csvHeader = useMemo(() => {
        const names: string[] = [];
        chain.forEach(fn => (fn.inputSchema || []).forEach(field => {
            if (!names.includes(field.name)) names.push(field.name);
        }));
        return names.join(',');
    }, [chain]);

    // Step navigation state.
    const stepIndex = STEPS.findIndex(s => s.id === activeStep);
    const rowCount = csvRows.split('\n').filter(line => line.trim() !== '').length;
    const hasInputData = rowCount > 0;
    // A step must be completed before the user can advance from it.
    const stepComplete: Record<StepId, boolean> = {
        functions: chain.length > 0,
        input: hasInputData,
        submit: jobId !== '',
    };
    const canGoBack = stepIndex > 0;
    const canGoNext = stepIndex < STEPS.length - 1 && stepComplete[activeStep];

    const handleOpenModal = async () => {
        setSearch('');
        setPage(0);

        // Use the cached list if we've already fetched successfully.
        if (functionsLoaded) {
            setModalOpen(true);
            return;
        }

        setError('');
        setLoading(true);
        try {
            const functions = await BatcherService.listFunctions(stage, realm);
            setAvailableFunctions(Array.isArray(functions) ? functions : []);
            setFunctionsLoaded(true);
            setModalOpen(true);
        } catch (e: any) {
            setError(e.message || 'Failed to load functions');
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setPage(0);
    };

    const handleAddFunction = (fn: FunctionMetadata) => {
        setChain(prev => [...prev, fn]);
        setModalOpen(false);
    };

    const handleRemoveFunction = (index: number) => {
        setChain(prev => prev.filter((_, i) => i !== index));
    };

    const handleBack = () => {
        if (canGoBack) setActiveStep(STEPS[stepIndex - 1].id);
    };

    const handleNext = () => {
        if (canGoNext) setActiveStep(STEPS[stepIndex + 1].id);
    };

    const handleSubmit = async () => {
        setSubmitError('');
        setSubmitting(true);
        // Reset any previous job result before submitting a new one.
        setJobId('');
        setJobStatus('');
        setDownloadUrl('');
        setJobError('');
        setSubmittedRowCount(rowCount);
        try {
            const csv = `${csvHeader}\n${csvRows}`;
            const functionChain = chain.map(fn => ({
                functionIdentifier: fn.functionIdentifier,
                additionalArguments: {},
            }));
            const id = await BatcherService.uploadAndSubmit(stage, realm, csv, functionChain);
            setJobId(id);
        } catch (e: any) {
            setSubmitError(e.message || 'Failed to submit job');
        } finally {
            setSubmitting(false);
        }
    };

    // Poll the job status once a job has been submitted, until it reaches a terminal state.
    useEffect(() => {
        if (!jobId) return undefined;

        let cancelled = false;
        let intervalId: ReturnType<typeof setInterval>;

        const poll = async () => {
            try {
                const result = await BatcherService.getJobResult(stage, realm, jobId);
                if (cancelled) return;
                setJobStatus(result.status);
                if (result.downloadUrl) setDownloadUrl(result.downloadUrl);
                if (result.error) setJobError(result.error);
                if (TERMINAL_JOB_STATUSES.includes(result.status)) clearInterval(intervalId);
            } catch (e: any) {
                if (cancelled) return;
                setJobError(e.message || 'Failed to fetch job status');
                clearInterval(intervalId);
            }
        };

        poll();
        intervalId = setInterval(poll, JOB_POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [jobId, stage, realm]);

    return (
        <div className="batcher-page">
            <div className="batcher-header">
                <h1 className="batcher-title">Batcher</h1>
                <p className="batcher-subtitle">Select functions and provide input rows to run them as a chain.</p>
            </div>

            {/* Display-only step header. Tabs are not clickable — use Back/Next to navigate. */}
            <div className="batcher-steps" role="tablist" aria-label="Batcher steps">
                {STEPS.map((step, index) => (
                    <div
                        key={step.id}
                        role="tab"
                        aria-selected={step.id === activeStep}
                        className={`batcher-step${step.id === activeStep ? ' batcher-step-active' : ''}`}
                    >
                        <span className="batcher-step-number">{index + 1}</span>
                        <span className="batcher-step-label">{step.label}</span>
                    </div>
                ))}
            </div>

            {activeStep === 'functions' && (
                <div className="batcher-panel">
                    <div className="batcher-panel-header">
                        <span className="batcher-panel-title">Functions</span>
                        <KatButton
                            variant="secondary"
                            onClick={handleOpenModal}
                            disabled={loading}
                        >
                            {loading ? 'Loading...' : '+ Add a function to chain'}
                        </KatButton>
                    </div>

                    {error && <div className="batcher-error">{error}</div>}

                    {chain.length === 0 ? (
                        <div className="batcher-empty">No functions added</div>
                    ) : (
                        <ol className="batcher-chain">
                            {chain.map((fn, index) => (
                                <li key={`${fn.functionIdentifier}-${index}`} className="batcher-chain-item">
                                    <div className="batcher-chain-item-content">
                                    <div className="batcher-chain-item-main">
                                        <span className="batcher-chain-item-name">{fn.functionName}</span>
                                        <KatButton
                                            variant="link"
                                            onClick={() => handleRemoveFunction(index)}
                                        >
                                            Remove
                                        </KatButton>
                                    </div>
                                    <div className="batcher-chain-item-fields">
                                        {fn.inputSchema && fn.inputSchema.length > 0 ? (
                                            fn.inputSchema.map(field => (
                                                <span
                                                    key={field.name}
                                                    className="batcher-field-chip"
                                                    title={field.description}
                                                >
                                                    {field.name}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="batcher-fields-label">No CSV input fields required</span>
                                        )}
                                    </div>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            )}

            {activeStep === 'input' && (
                <div className="batcher-panel">
                    <div className="batcher-input-body">
                        <div className="batcher-input-field">
                            <label className="batcher-input-label" htmlFor="batcher-csv-header">
                                CSV header (columns)
                            </label>
                            <p className="batcher-input-hint">
                                Determined by the selected functions&apos; required fields.
                            </p>
                            <input
                                id="batcher-csv-header"
                                type="text"
                                className="batcher-csv-header"
                                value={csvHeader}
                                readOnly
                            />
                        </div>

                        <div className="batcher-input-field">
                            <label className="batcher-input-label" htmlFor="batcher-csv-rows">
                                CSV rows
                            </label>
                            <p className="batcher-input-hint">
                                One row per line, values comma-separated in the same order as the header above.
                            </p>
                            <textarea
                                id="batcher-csv-rows"
                                className="batcher-csv-rows"
                                value={csvRows}
                                onChange={e => setCsvRows(e.target.value)}
                                placeholder={csvHeader ? `${csvHeader.split(',').map(() => 'value').join(',')}` : 'value1,value2,value3'}
                                rows={12}
                            />
                        </div>
                    </div>
                </div>
            )}

            {activeStep === 'submit' && (
                <div className="batcher-panel">
                    <div className="batcher-submit-body">
                        <div className="batcher-submit-summary">
                            <div className="batcher-submit-summary-row">
                                <span className="batcher-submit-summary-label">Functions</span>
                                <span className="batcher-submit-summary-value">
                                    {chain.map(fn => fn.functionName).join(' → ')}
                                </span>
                            </div>
                            <div className="batcher-submit-summary-row">
                                <span className="batcher-submit-summary-label">Input rows</span>
                                <span className="batcher-submit-summary-value">{rowCount}</span>
                            </div>
                        </div>

                        {submitError && <div className="batcher-error">{submitError}</div>}

                        {jobId ? (
                            <div className="batcher-job-result">
                                <div className="batcher-submit-success">
                                    Job submitted. Job ID: <span className="batcher-job-id">{jobId}</span>
                                </div>

                                <div className="batcher-job-status-row">
                                    {!TERMINAL_JOB_STATUSES.includes(jobStatus) && !jobError && (
                                        <KatSpinner size="small" />
                                    )}
                                    <span className="batcher-job-status">
                                        Status: {jobStatus || 'PENDING'}
                                    </span>
                                </div>

                                {!jobError && (
                                    <div className="batcher-progress">
                                        <div className="batcher-progress-track">
                                            <div
                                                className="batcher-progress-fill"
                                                style={{ width: `${jobStatus === 'SUCCEEDED' ? 100 : 0}%` }}
                                            />
                                        </div>
                                        <span className="batcher-progress-text">
                                            {jobStatus === 'SUCCEEDED' ? submittedRowCount : 0}/{submittedRowCount} rows processed
                                        </span>
                                    </div>
                                )}

                                {jobError && <div className="batcher-error">{jobError}</div>}

                                {jobStatus === 'SUCCEEDED' && downloadUrl && (
                                    <a
                                        className="batcher-download-button"
                                        href={downloadUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        download
                                    >
                                        Download result CSV
                                    </a>
                                )}
                            </div>
                        ) : (
                            <KatButton
                                className="batcher-submit-button"
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? 'Submitting...' : 'Submit job'}
                            </KatButton>
                        )}
                    </div>
                </div>
            )}

            <div className="batcher-nav">
                <KatButton className="batcher-nav-back" variant="secondary" onClick={handleBack} disabled={!canGoBack}>
                    Back
                </KatButton>
                <KatButton className="batcher-nav-next" variant="primary" onClick={handleNext} disabled={!canGoNext}>
                    Next
                </KatButton>
            </div>

            {modalOpen && (
            <KatModal
                visible={modalOpen}
                title="Add functions to chain"
                onClose={() => setModalOpen(false)}
            >
                <div className="batcher-modal-body">
                    <input
                        type="text"
                        className="batcher-search"
                        placeholder="Search functions by name..."
                        value={search}
                        onChange={e => handleSearchChange(e.target.value)}
                    />

                    <div className="batcher-function-list">
                        {filteredFunctions.length === 0 ? (
                            <div className="batcher-picker-empty">
                                {availableFunctions.length === 0 ? 'No functions available' : 'No functions match your search'}
                            </div>
                        ) : (
                            pageFunctions.map(fn => (
                                <button
                                    key={fn.functionIdentifier}
                                    type="button"
                                    className="batcher-function-item"
                                    onClick={() => handleAddFunction(fn)}
                                >
                                    <span className="batcher-function-item-name">{fn.functionName}</span>
                                </button>
                            ))
                        )}
                    </div>

                    {totalPages > 1 && (
                        <div className="batcher-pagination">
                            <KatButton
                                variant="link"
                                disabled={currentPage === 0}
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                            >
                                Previous
                            </KatButton>
                            <span className="batcher-pagination-info">
                                Page {currentPage + 1} of {totalPages}
                            </span>
                            <KatButton
                                variant="link"
                                disabled={currentPage >= totalPages - 1}
                                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                            >
                                Next
                            </KatButton>
                        </div>
                    )}
                </div>

                <div slot="footer" className="batcher-modal-footer">
                    <KatButton variant="link" onClick={() => setModalOpen(false)}>Cancel</KatButton>
                </div>
            </KatModal>
            )}
        </div>
    );
};

export default BatcherPage;
